import React, { useLayoutEffect, useState, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import debug from 'debug';

const log = debug('rois:components:forms:work-equipment');

if (import.meta.env.DEV) {
  debug.enable('rois:components:forms:work-equipment*');
}

// Styles
import {
  AdviceContainer,
  ButtonContainer,
  ContainerError,
  GeneralContainer,
  TitleContainerWithButton,
} from '../styles';
import {
  WorkEquipmentSections,
  EquipmentSection,
  FormContainer,
} from './styles';

// Components
import { Typography } from '../../../display/Typography';
import SimpleBackgroundSwitch from '../../../inputs/buttons/SwitchButton/SimpleBackground';
import { MainButton } from '../../../inputs/buttons/MainButton';
import { WorkVehicleSelectionTable } from '../../../display/tables/formTables/WorkVehicleSelectionTable';
import { MachineSelectionTable } from '../../../display/tables/formTables/MachineSelectionTable';
import { AddVehicleManagementForm } from '../../AddVehicleManagementForm';
import { AddMachineryManagementForm } from '../../AddMachineryManagementForm';

// Types
import {
  WorkEquipmentFormProps,
  WorkEquipmentFormValues,
  WorkVehicle,
  AuxMachinery,
  dataTableVehicle,
  dataTableMachinery,
} from './types';
import { OrderState } from '../../../../types/orders';

// Utils
import { useCacheFormInSessionStorage } from '../../../../hooks/useCacheForm';
import { handleAdd, handleDelete, handleUpdate } from '../formTableUtils';
import {
  compressSessionStorage,
  decompressSessionStorage,
} from '../../../../utils/sessionStorage';
import { DataState } from '../types';

// GQL
import { client } from '../../../../gql/client';
import { CREATE_NEW_VEHICLE } from '../../../../gql/mutations/vehicles';
import { CREATE_NEW_AUX_MACHINERY } from '../../../../gql/mutations/machinery';
import {
  appendAuxMachineToCache,
  appendVehicleToCache,
} from '../../../../gql/cacheHelpers';

// Types - Vehicle Enums
import { VehicleOwnership, VehicleUsage } from '../../../../types/vehicles';

// Constants
import { subCacheSuffix } from '../constants';

// ⚠️ SINCRONIZACIÓN: Eventos de vehículos
import {
  useVehicleEvents,
  dispatchVehicleCreated,
} from '../../../../utils/vehicleEvents';

// ⚠️ SINCRONIZACIÓN: Sistema de exclusiones
import {
  addExcludedVehicle,
  removeExcludedVehicle,
} from '../../../../utils/excludedVehiclesSync';

const WorkEquipmentForm = ({
  initialData,
  onFormSubmit,
  sessionStorageKey,
  updateKey,
  textButton,
}: WorkEquipmentFormProps) => {
  const { t } = useTranslation(['forms', 'common']);
  // ✅ Para activar validación visual al navegar desde modal de errores
  const locationState = useLocation();
  // El switch representa "¿tenemos Maquinaria Auxiliar?" y abre/cierra TODA la
  // sección (Vehículos de Obra + Maquinaria Auxiliar). Por defecto arranca
  // CERRADO; solo abierto si la actuación ya tiene maquinaria auxiliar guardada.
  // Los vehículos de obra se sincronizan desde Operarios y se persisten SIEMPRE,
  // esté la sección abierta o cerrada (ver saveData).
  const hasAuxiliaryMachinery =
    Object.keys(initialData?.machineryTable ?? {}).length > 0;

  const { control, handleSubmit, setValue, register, getValues } =
    useForm<WorkEquipmentFormValues>({
      defaultValues: {
        ...initialData,
        isWorkEquipmentDisplayed: initialData.isWorkEquipmentDisplayed
          ? initialData.isWorkEquipmentDisplayed
          : hasAuxiliaryMachinery
            ? 'true'
            : 'false',
      },
    });

  const [dataFromVehiclesTable, setDataFromVehiclesTable] = useState<
    null | dataTableVehicle[]
  >(null);
  const [dataFromMachineryTable, setDataFromMachineryTable] = useState<
    null | dataTableMachinery[]
  >(null);
  // Arranca cerrado; useLayoutEffect lo recalcula con switchStateValue() antes de pintar.
  const [switchState, setSwitchState] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const updateVehiclesTableKey = 'vehiclesData';
  const updateMachineryTableKey = 'machineryData';

  useCacheFormInSessionStorage(sessionStorageKey, control);

  // ⚠️ SINCRONIZACIÓN: Escuchar cuando se asigna conductor en OperatorsAndVehicles
  useVehicleEvents(
    undefined, // No escuchamos creación de vehículos aquí
    ({ vehicleId, driverId }) => {
      // ⚠️ SINCRONIZACIÓN: Si se asigna conductor, QUITAR de exclusiones
      // Esto permite que el vehículo pueda volver a sincronizarse automáticamente
      const isValidation = sessionStorageKey?.includes('VALIDATION');
      removeExcludedVehicle(vehicleId, isValidation);
      log(
        `✅ Vehicle ${vehicleId} removed from exclusions (driver assigned)`
      );

      // ⚠️ VALIDACIÓN 1: Verificar que el vehículo existe en catálogo
      const vehicle = initialData.vehicles?.find(
        (v: any) => v.id === vehicleId
      );
      if (!vehicle) {
        log(`⚠️ Vehicle not found in catalog: ${vehicleId}`);
        return;
      }

      // ⚠️ VALIDACIÓN 2: Verificar que NO está ya en la tabla
      const isAlreadyInTable = dataFromVehiclesTable?.some(
        (v: any) => v.id === vehicleId
      );
      if (isAlreadyInTable) {
        log(`Vehicle already in table: ${vehicleId}`);
        return;
      }

      // ⚠️ VALIDACIÓN 3: Verificar que es CONSTRUCTION
      if (vehicle.usage !== 'CONSTRUCTION') {
        log(`⚠️ Vehicle is not CONSTRUCTION: ${vehicleId}`);
        return;
      }

      const newVehicleForTable: WorkVehicle = {
        vehicleId: vehicle.id,
        plate: vehicle.plate,
        model: vehicle.model,
        category: vehicle.category,
        ownership: vehicle.ownership,
        usage: vehicle.usage,
      };

      const newVehicleForDisplay: dataTableVehicle = {
        id: vehicle.id,
        plate: vehicle.plate,
        model: vehicle.model,
        category: vehicle.category,
        ownership: vehicle.ownership,
        usage: vehicle.usage,
      };

      setDataFromVehiclesTable((prev) => [
        ...(prev || []),
        newVehicleForDisplay,
      ]);

      // Actualizar sessionStorage via form (vehiclesTable es un objeto key-value)
      // ✅ Usar el ID del vehículo como clave directamente (sin prefijos ni timestamps)
      const currentForm = getValues();
      const currentVehiclesTable = currentForm.vehiclesTable || {};
      const vehicleKey = vehicle.id; // ✅ Simplemente el ID
      setValue('vehiclesTable', {
        ...currentVehiclesTable,
        [vehicleKey]: newVehicleForTable,
      });

      log(
        `Vehicle added from driver assignment: ${vehicle.plate}`
      );
    }
  );

  const switchStateValue = () => {
    const { isWorkEquipmentDisplayed } = initialData;

    if (isWorkEquipmentDisplayed) {
      return isWorkEquipmentDisplayed === 'true';
    }

    // Sin valor previo: cerrado por defecto, salvo que ya haya maquinaria auxiliar.
    const initial = hasAuxiliaryMachinery ? 'true' : 'false';
    setValue('isWorkEquipmentDisplayed', initial);
    return hasAuxiliaryMachinery;
  };

  useLayoutEffect(() => {
    log(
      '🔄 useLayoutEffect - Iniciando carga de datos iniciales'
    );
    log(
      '🔍 initialData completo:',
      initialData
    );
    log(
      '🔍 initialData.vehiclesTable:',
      initialData?.vehiclesTable
    );
    log(
      '🔍 initialData.machineryTable:',
      initialData?.machineryTable
    );

    // ✅ Mapear vehiclesTable a dataTableVehicle[] incluyendo el id
    const vehiclesData = (Object.values(initialData?.vehiclesTable ?? {}) ?? [])
      .filter((element) => element)
      .map((v: WorkVehicle) => {
        log(
          '🚗 Mapeando vehículo:',
          v
        );
        // 🔍 CLAVE: El objeto en el cache puede tener 'id' o 'vehicleId' dependiendo de si viene del servidor o es nuevo
        const vehicleId = v.vehicleId || (v as any).id;
        log(
          '🚗 vehicleId extraído:',
          vehicleId
        );
        return {
          id: vehicleId, // ✅ Usar el ID correcto
          plate: v.plate,
          model: v.model,
          category: v.category || '',
          ownership: v.ownership,
          usage: v.usage,
          vehicleOccupantId: v.vehicleOccupantId, // 🔍 IMPORTANTE: Mantener el vehicleOccupantId para el borrado
        };
      }) as dataTableVehicle[];

    // ✅ Mapear machineryTable a dataTableMachinery[] incluyendo el id
    const machineryData = (
      Object.values(initialData?.machineryTable ?? {}) ?? []
    )
      .filter((element) => element)
      .map((m: AuxMachinery) => {
        log(
          '🔧 Mapeando maquinaria:',
          m
        );
        // 🔍 CLAVE: Priorizar 'id' (MongoDB ID) sobre 'auxMachineId' (código de máquina)
        // El ID de MongoDB es la clave primaria real que necesitamos para identificar el registro
        const machineId = m.id || m.auxMachineId;
        log('🔧 m.id:', m.id);
        log(
          '🔧 m.auxMachineId:',
          m.auxMachineId
        );
        log(
          '🔧 machineId FINAL extraído:',
          machineId
        );
        return {
          id: machineId, // ✅ Usar el ID de MongoDB
          auxMachineId: m.auxMachineId, // ✅ Mantener el código de máquina separado
          auxMachineUnitId: m.auxMachineUnitId, // 🔍 IMPORTANTE: Mantener el auxMachineUnitId para el borrado
          description: m.description,
          type: m.type,
          typeOfMeasurement: m.typeOfMeasurement,
          ut: m.ut,
          isInternal: m.isInternal,
        };
      }) as dataTableMachinery[];

    log(
      '✅ VehiclesData mapeado:',
      vehiclesData
    );
    log(
      '✅ MachineryData mapeado:',
      machineryData
    );

    setDataFromVehiclesTable(vehiclesData);
    setDataFromMachineryTable(machineryData);
    setSwitchState(switchStateValue());

    log('✅ Estados actualizados');
  }, []);

  const handleChange = () => {
    if (switchState) {
      // Apagar el switch = "no tenemos maquinaria auxiliar". Limpia SOLO la
      // maquinaria. Los vehículos de obra dependen de Operarios (no del switch)
      // y NUNCA se borran aquí: deben persistir aunque la sección esté cerrada.
      setSwitchState(false);
      setValue('isWorkEquipmentDisplayed', 'false');
      setValue('machineryTable', null);
      if (updateKey) {
        const machineryIdsToDelete = Object.values(
          initialData?.machineryTable ?? {}
        ).map((item: any) => item.auxMachineUnitId);

        const currentCacheData = sessionStorage.getItem(updateKey)
          ? JSON.parse(
              decompressSessionStorage(
                sessionStorage.getItem(updateKey) as string
              )
            )
          : {};

        currentCacheData[updateMachineryTableKey] ??= {};
        currentCacheData[updateMachineryTableKey][DataState.deleted] =
          machineryIdsToDelete;
        currentCacheData[updateMachineryTableKey][DataState.added] = [];
        currentCacheData[updateMachineryTableKey][DataState.updated] = [];

        sessionStorage.setItem(
          updateKey,
          compressSessionStorage(JSON.stringify(currentCacheData))
        );
      }
      setDataFromMachineryTable([]);
    } else {
      setSwitchState(true);
      setValue('isWorkEquipmentDisplayed', 'true');
    }
  };

  const validateWorkEquipmentTables = () => {
    const vehiclesTableValues = getValues('vehiclesTable');
    const machineryTableValues = getValues('machineryTable');

    const existsAtLeastOneValue =
      Object.entries(vehiclesTableValues ?? {}).some(
        ([, element]) => !!element
      ) ||
      Object.entries(machineryTableValues ?? {}).some(
        ([, element]) => !!element
      );

    // ✅ Los vehículos NO necesitan campo 'ut', solo verificamos que existan los datos básicos
    const allVehiclesAreValid = Object.entries(vehiclesTableValues ?? {}).every(
      ([, element]) =>
        element === undefined
          ? true
          : !!element && !!element.plate && !!element.model
    );

    // ✅ La maquinaria SÍ necesita el campo 'ut' (unidades de medida)
    const allMachineryHaveUnits = Object.entries(
      machineryTableValues ?? {}
    ).every(([, element]) =>
      element === undefined ? true : !!element && Number(element.ut) > 0
    );

    if (!existsAtLeastOneValue) {
      setError(
        t(
          'workEquipment.atLeastOneElement',
          'Afegiu almenys un element a la taula principal'
        )
      );
      return false;
    }

    if (!allVehiclesAreValid || !allMachineryHaveUnits) {
      setError(
        t(
          'workEquipment.fillAllFields',
          'Si us plau, ompliu tots els camps dels elements afegits'
        )
      );
      return false;
    }

    return true;
  };

  const checkTableValues = () => {
    if (switchState) {
      return validateWorkEquipmentTables();
    }
    return true;
  };

  // ✅ Activar validación visual al navegar desde modal de errores
  useEffect(() => {
    if (locationState.state?.validateOnMount && switchState) {
      log('🚨 Triggering validation on mount');
      checkTableValues();
    }
  }, [locationState.state?.validateOnMount, switchState]);

  // Vehicle handlers
  const handleUpdateVehicle = (
    eventValue: string | number,
    id: string,
    unitId: string,
    completeId: string
  ) => {
    setValue(completeId as keyof WorkEquipmentFormValues, eventValue as any);

    if (!updateKey) {
      return;
    }

    handleUpdate(
      eventValue,
      id,
      unitId,
      initialData,
      updateVehiclesTableKey,
      'vehiclesTable',
      updateKey
    );
  };

  const handleAddVehicle = (id: string) => {
    log('🔍 handleAddVehicle - Añadiendo vehículo:', id);
    if (updateKey) {
      handleAdd(id, updateVehiclesTableKey, updateKey);
      log('✅ Vehículo añadido al cache');
    }
  };

  // ⚠️ CRÍTICO: Este callback debe ejecutarse SIEMPRE (NewAction y ActionsValidation)
  // No depende de updateKey para el sistema de exclusiones
  const handleDeleteVehicle = (id: string, unitId: string) => {
    log(
      '🔍 handleDeleteVehicle ============ INICIO ============'
    );
    log('🔍 Params:', {
      id,
      unitId,
      updateKey,
      sessionStorageKey,
    });

    // ⚠️ PARTE 1: Actualizar sessionStorage de cambios pendientes (solo si updateKey existe)
    if (updateKey) {
      log(
        '🔍 Llamando a handleDelete de formTableUtils...'
      );
      handleDelete(id, unitId, updateVehiclesTableKey, updateKey);
      log(
        '✅ Vehículo borrado del cache de cambios'
      );
    } else {
      log(
        '⚠️ updateKey is undefined (NewAction mode), saltando handleDelete'
      );
    }

    // ⚠️ PARTE 2: Añadir a exclusiones SIEMPRE (independiente de updateKey)
    // Esto evita que el vehículo vuelva a aparecer automáticamente
    const isValidation = sessionStorageKey?.includes('VALIDATION');
    log(
      '🔍 isValidation:',
      isValidation
    );
    log(
      '🔍 Llamando a addExcludedVehicle...'
    );
    addExcludedVehicle(id, isValidation);
    log(
      `✅ Vehículo ${id} añadido a exclusiones (${isValidation ? 'validation' : 'newAction'})`
    );
    log(
      '🔍 handleDeleteVehicle ============ FIN ============'
    );
  };

  // Machinery handlers
  const handleUpdateMachinery = (
    eventValue: string | number,
    id: string,
    unitId: string,
    completeId: string
  ) => {
    setValue(completeId as keyof WorkEquipmentFormValues, eventValue as any);

    if (!updateKey) {
      return;
    }

    handleUpdate(
      eventValue,
      id,
      unitId,
      initialData,
      updateMachineryTableKey,
      'machineryTable',
      updateKey
    );
  };

  const handleAddMachinery = (id: string) => {
    log('🔍 handleAddMachinery - Añadiendo maquinaria:', id);
    if (updateKey) {
      handleAdd(id, updateMachineryTableKey, updateKey);
      log('✅ Maquinaria añadida al cache');
    }
  };

  const handleDeleteMachinery = (id: string, unitId: string) => {
    log('🔍 handleDeleteMachinery - Borrando maquinaria:', {
      id,
      unitId,
    });
    if (!updateKey) {
      return;
    }

    handleDelete(id, unitId, updateMachineryTableKey, updateKey);
    log('✅ Maquinaria borrada del cache');
  };

  // Create vehicle handler
  const handleCreateVehicle = useCallback(
    async (data: any) => {
      try {
        // ⚠️ VALIDACIÓN 1: Verificar que la matrícula no existe ya
        const existingVehicle = initialData.vehicles?.find(
          (v: any) => v.plate.toUpperCase() === data.plate.toUpperCase()
        );

        if (existingVehicle) {
          log(`⚠️ Vehicle already exists: ${data.plate}`);
          return `El vehículo con matrícula ${data.plate} ya existe`;
        }

        // Find the category name from the category ID
        const categoryObj = initialData.vehicleCategories?.find(
          (cat) => cat.id === data.category
        );
        const categoryName = categoryObj?.name || null;

        // ✅ NUEVO: Para vehículos de obra, siempre usage=CONSTRUCTION
        // ownership depende del propósito seleccionado (si existe) o defaulta a INTERNAL
        const ownership = data.ownership || VehicleOwnership.INTERNAL;
        const usage = VehicleUsage.CONSTRUCTION; // Siempre CONSTRUCTION para vehículos de obra

        // Crear vehículo (ÚNICA creación en BD)
        const { data: mutationData } = await client.mutate({
          mutation: CREATE_NEW_VEHICLE,
          variables: {
            input: {
              plate: data.plate,
              model: data.model,
              vehicleCategoryIDs: [data.category],
              category: categoryName, // Category name as string
              isActive: true,
              isTrailer: false,
              ownership,
              usage,
              concessionId: data.concession ||
                (initialData.concessions?.length === 1 ? initialData.concessions[0].id : null),
              teamIDs: data.teamIDs || [],
            },
          },
          update(cache, { data: { createVehicle } }) {
            // C-11.3: helper compartido con sync offline (mismo path que
            // applyOperationCacheUpdate ejecuta al replay-ear `CreateVehicle`).
            appendVehicleToCache(cache, createVehicle);
          },
        });

        // ✅ Disparar evento SOLO si la creación fue exitosa
        if (mutationData?.createVehicle) {
          dispatchVehicleCreated(mutationData.createVehicle);
          log(
            `Vehicle created: ${mutationData.createVehicle.plate}`
          );
        }

        // ✅ Recargar página para actualizar lista de vehículos disponibles
        // (Similar a como funciona con maquinaria)
        // NOTA: Cuando el usuario navegue a "Operarios y Vehículos", el loader
        // traerá los datos frescos del servidor (fetchPolicy: 'network-only')
        window.location.reload();
      } catch (error: any) {
        // C-11.3: detectar mutation encolada offline. OfflineQueueLink lanza
        // Error con `offlineQueued: true` cuando guarda en IndexedDB. Sin
        // este check el form mostraba "Error al crear el vehículo" aunque
        // la mutation se encolara correctamente. Tras reconnect el sync
        // replay invoca `appendVehicleToCache` vía `applyOperationCacheUpdate`.
        if (
          error?.offlineQueued ||
          error?.networkError?.offlineQueued ||
          (error?.message ?? '').includes('Mutation queued for offline sync')
        ) {
          log(`Vehicle creation queued offline: ${data.plate}`);
          // No reload — la lista se actualizará al sync con el helper.
          return;
        }
        console.error('[WorkEquipment] Error creating vehicle:', error);
        return 'Error al crear el vehículo';
      }
    },
    [initialData.vehicleCategories, initialData.vehicles]
  );

  // Create machinery handler
  const handleCreateMachinery = useCallback(async (data: any) => {
    try {
      await client.mutate({
        mutation: CREATE_NEW_AUX_MACHINERY,
        variables: {
          input: {
            description: data.name,
            auxMachineTypeIDs: data.type,
            measureTypeIDs: data.typeOfMeasurement,
            isInternal: data.isInternal !== undefined ? data.isInternal : true,
          },
        },
        update(cache, { data: { createAuxMachine } }) {
          // C-11.3: helper compartido con sync offline.
          appendAuxMachineToCache(cache, createAuxMachine);
        },
      });

      // Refresh the page or update state
      window.location.reload();
    } catch (error: any) {
      // C-11.3: detectar mutation encolada offline (igual que vehículos).
      if (
        error?.offlineQueued ||
        error?.networkError?.offlineQueued ||
        (error?.message ?? '').includes('Mutation queued for offline sync')
      ) {
        return;
      }
      console.error('Error creating machinery:', error);
      return 'Error al crear la maquinaria';
    }
  }, []);

  const isDisabled =
    initialData?.parentExpeditionOrderStatus === OrderState.Annulled ||
    initialData?.parentExpeditionOrderStatus === OrderState.End ||
    initialData?.state === OrderState.Annulled;

  // 🔑 DEBUG: Verificar si updateKey y sessionStorageKey están definidos
  log('🔑 RENDER - updateKey:', updateKey);
  log(
    '🔑 RENDER - sessionStorageKey:',
    sessionStorageKey
  );
  log(
    '🔑 RENDER - onDelete callback:',
    updateKey ? 'DEFINIDO (handleDeleteVehicle)' : 'UNDEFINED'
  );

  return (
    <GeneralContainer
      onSubmit={(e) => {
        if (checkTableValues()) {
          handleSubmit(onFormSubmit)(e);
        } else {
          validateWorkEquipmentTables();
          e.preventDefault();
        }
      }}
    >
      <TitleContainerWithButton>
        {/* El switch gobierna toda la sección (Vehículos de Obra + Maquinaria
            Auxiliar). Arranca cerrado por defecto (solo abierto al editar si la
            actuación ya tiene maquinaria auxiliar). Los vehículos de obra se
            eligen en Operarios y se reflejan/persisten igualmente, esté la
            sección abierta o cerrada. NO se cambia el texto del título. */}
        <Typography variant="semiBold" component="h3" size="L">
          {t('workEquipment.workVehicles', 'Vehículos de Obra')}
        </Typography>
        <SimpleBackgroundSwitch
          changeState={handleChange}
          state={switchState}
          disabled={isDisabled}
        />
      </TitleContainerWithButton>

      {switchState &&
      dataFromVehiclesTable !== null &&
      dataFromMachineryTable !== null ? (
        <WorkEquipmentSections>
          {/* SECCIÓN 1: VEHÍCULOS DE OBRA */}
          <EquipmentSection>
            <FormContainer>
              <AddVehicleManagementForm
                initialData={{
                  cacheData: {
                    id: '',
                    plate: '',
                    model: '',
                    category: '',
                    isActive: true,
                    ownership: VehicleOwnership.INTERNAL,
                    usage: VehicleUsage.CONSTRUCTION,
                    isTrailer: false,
                  },
                  vehicleCategories: initialData.vehicleCategories || [],
                  concessions: initialData.concessions || [],
                  teams: initialData.teams || [],
                  activeVehicles: [],
                  inactiveVehicles: [],
                }}
                onFormSubmit={handleCreateVehicle}
                showOnlyOwnershipSelector={true}
                requireConcessionMode="internal"
                namingPersistForm={`${sessionStorageKey}${subCacheSuffix.VEHICLE}`}
                hideTitle={true}
              />
            </FormContainer>

            <WorkVehicleSelectionTable
              data={initialData}
              teams={initialData?.teams || []}
              register={register}
              setValue={setValue}
              defaultValues={dataFromVehiclesTable}
              onUpdate={handleUpdateVehicle}
              onDelete={handleDeleteVehicle} // ⚠️ SIEMPRE definido (maneja exclusiones incluso sin updateKey)
              onAdd={updateKey ? handleAddVehicle : undefined}
            />
          </EquipmentSection>

          {/* SECCIÓN 2: MAQUINARIA AUXILIAR */}
          <EquipmentSection>
            <Typography variant="semiBold" size="L">
              {t('workEquipment.auxiliaryMachinery', 'Maquinaria Auxiliar')}
            </Typography>

            <FormContainer>
              <AddMachineryManagementForm
                initialData={{
                  name: '',
                  type: '',
                  typeOfMeasurement: '',
                  isInternal: true,
                }}
                data={{
                  auxMachinesData: [],
                  auxMachineTypes: initialData.auxMachineTypes || [],
                  measureTypes: initialData.measureTypes || [],
                }}
                onFormSubmit={handleCreateMachinery}
                showInternalCheckbox={true}
                namingPersistForm={`${sessionStorageKey}${subCacheSuffix.MACHINERY}`}
                hideTitle={true}
              />
            </FormContainer>

            <MachineSelectionTable
              data={{
                isMachineryTableDisplayed: switchState ? 'true' : 'false',
                machineryTable: null,
                machines: initialData.machines || [],
                breadCrumbsData: initialData.breadCrumbsData,
                parentExpeditionOrderStatus: (initialData as any)
                  .parentExpeditionOrderStatus,
                state: (initialData as any).state,
              }}
              teams={initialData?.teams || []}
              register={register}
              setValue={setValue}
              defaultValues={dataFromMachineryTable}
              onUpdate={handleUpdateMachinery}
              onDelete={updateKey ? handleDeleteMachinery : undefined}
              onAdd={updateKey ? handleAddMachinery : undefined}
            />
          </EquipmentSection>
        </WorkEquipmentSections>
      ) : (
        <AdviceContainer>
          <Typography component="p" variant="semiBold" colorText="greyDark">
            {t(
              'workEquipment.noEquipment',
              'No se ha especificado maquinaria de obra'
            )}
          </Typography>
        </AdviceContainer>
      )}

      {error && switchState && (
        <ContainerError>
          <Typography component="p" size="S" colorText="lightCoralRed">
            {error}
          </Typography>
        </ContainerError>
      )}

      <ButtonContainer>
        <MainButton
          text={
            isDisabled
              ? t('common:buttons.continue')
              : textButton || t('common:buttons.continue')
          }
          type="submit"
        />
      </ButtonContainer>
    </GeneralContainer>
  );
};

export default WorkEquipmentForm;
